import React, { useState } from 'react';
import { normalizarRefeicoes, formatQuantidade } from '../lib/dietaUtils';
import { gerarPdfPlanoAlimentar } from '../lib/pdfGenerator';
import { IconeAlimento, IconeRefeicao, IconDroplet, IconFlame, IconDumbbell, IconRefreshCw, IconClock, IconDownload } from './icons/FoodIcons';

// Mesma renderização usada na aba Nutrição do app da aluna — usado também
// como pré-visualização no admin, pra garantir que os dois são idênticos.
const TX = {
  pt: { agua: 'Água', meta: 'meta', refeicoes: 'Refeições', trocarPor: 'alternativas', calorias: 'Calorias', proteina: 'Proteína', cadastradoEm: 'Cadastrado em', verMais: 'ver mais', verMenos: 'ver menos', dicasPraticas: 'Dicas práticas', verTodasDicas: 'ver todas as dicas', ocultarDicas: 'ocultar dicas extras', orientacaoBabi: 'Orientação da Babi', lerCompleta: 'ler orientação completa', recolher: 'recolher' },
  en: { agua: 'Water', meta: 'target', refeicoes: 'Meals', trocarPor: 'alternatives', calorias: 'Calories', proteina: 'Protein', cadastradoEm: 'Registered on', verMais: 'see more', verMenos: 'see less', dicasPraticas: 'Practical tips', verTodasDicas: 'see all tips', ocultarDicas: 'hide extra tips', orientacaoBabi: "Babi's note", lerCompleta: 'read full note', recolher: 'collapse' },
};

function separarOrientacaoAgua(texto = '') {
  const limpo = texto.replace(/\s+/g, ' ').trim();
  if (!limpo) return { resumo: '', itens: [] };

  const [resumoBruto, estrategiasBrutas = ''] = limpo.split(/Estratégias:/i);
  const blocos = estrategiasBrutas
    .replace(/\s+(Beba|Tome|Utilize|Se preferir|Observe|Associe|Ao acordar|Antes do|Antes da|Durante o|Durante a)/g, '||$1')
    .split('||')
    .map(item => item.trim().replace(/[.;]$/, ''))
    .filter(Boolean);

  return {
    resumo: resumoBruto.trim() || limpo,
    itens: blocos,
  };
}

function horarioParaMinutos(horario = '') {
  const match = String(horario).match(/(\d{1,2})[:h](\d{2})/i);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function proximaRefeicaoIndex(refeicoes) {
  const agora = new Date();
  const minutosAgora = agora.getHours() * 60 + agora.getMinutes();
  const index = refeicoes.findIndex(ref => {
    const minutos = horarioParaMinutos(ref.horario);
    return minutos !== null && minutos >= minutosAgora;
  });
  return index >= 0 ? index : 0;
}

function BaixarPdfBtn({ onClick, label }) {
  const [loading, setLoading] = useState(false);
  async function handleClick() {
    setLoading(true);
    try { await onClick(); } finally { setLoading(false); }
  }
  return (
    <button onClick={handleClick} disabled={loading}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', background: 'transparent', border: '1.5px solid var(--accent)', borderRadius: '100px', color: 'var(--accent)', fontSize: '11px', fontWeight: 600, cursor: loading ? 'wait' : 'pointer', fontFamily: "'Inter',sans-serif", opacity: loading ? 0.6 : 1, flexShrink: 0 }}>
      {loading ? '...' : <><IconDownload size={12} /> {label}</>}
    </button>
  );
}

function AguaCard({ dieta, tx }) {
  const [aberto, setAberto] = useState(false);
  const [todasDicas, setTodasDicas] = useState(false);
  if (!dieta.agua?.instrucao && !dieta.agua?.meta) return null;
  const orientacao = separarOrientacaoAgua(dieta.agua.instrucao);
  const temItens = orientacao.itens.length > 0;
  const dicasVisiveis = todasDicas ? orientacao.itens : orientacao.itens.slice(0, 4);
  const dicasRestantes = Math.max(orientacao.itens.length - 4, 0);
  return (
    <div style={{ marginBottom: '16px', padding: '14px 16px', background: 'rgba(62,110,147,0.08)', border: '1px solid rgba(62,110,147,0.14)', borderRadius: '18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: dieta.agua.instrucao ? '10px' : 0 }}>
        <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'rgba(62,110,147,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <IconDroplet size={17} color="#3E6E93" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#3E6E93', lineHeight: 1.2 }}>{tx.agua}</div>
          {dieta.agua.meta && <div style={{ fontSize: '11px', color: 'var(--text-2)', marginTop: '2px' }}>{tx.meta} {dieta.agua.meta}</div>}
        </div>
      </div>
      {dieta.agua.instrucao && (
        <>
          {!aberto && (
            <div style={{
              fontSize: '12px', color: '#6f6660', lineHeight: 1.55,
              display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden',
              WebkitLineClamp: 3,
            }}>{orientacao.resumo}</div>
          )}
          {aberto && (
            <div style={{ display: 'grid', gap: '10px' }}>
              {orientacao.resumo && (
                <p style={{ margin: 0, fontSize: '12px', color: '#6f6660', lineHeight: 1.58 }}>{orientacao.resumo}</p>
              )}
              {temItens && (
                <div style={{ display: 'grid', gap: '7px' }}>
                  <div style={{ fontSize: '10px', color: '#3E6E93', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tx.dicasPraticas}</div>
                  {dicasVisiveis.map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '8px 10px', borderRadius: '12px', background: 'rgba(255,255,255,0.72)' }}>
                      <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(62,110,147,0.12)', color: '#3E6E93', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '10px', fontWeight: 700 }}>{i + 1}</span>
                      <span style={{ fontSize: '12px', color: '#6f6660', lineHeight: 1.45 }}>{item}</span>
                    </div>
                  ))}
                  {dicasRestantes > 0 && (
                    <button type="button" onClick={() => setTodasDicas(v => !v)}
                      style={{ width: 'fit-content', marginTop: '1px', background: 'rgba(62,110,147,0.08)', border: 'none', borderRadius: '100px', padding: '6px 10px', color: '#3E6E93', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                      {todasDicas ? tx.ocultarDicas : `${tx.verTodasDicas} (+${dicasRestantes})`}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <button type="button" onClick={() => setAberto(v => !v)}
            style={{ marginTop: '8px', background: 'none', border: 'none', padding: 0, color: '#3E6E93', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
            {aberto ? tx.verMenos : tx.verMais}
          </button>
        </>
      )}
    </div>
  );
}

function NotaBabi({ texto, tx }) {
  const [aberta, setAberta] = useState(false);
  if (!texto) return null;
  return (
    <div style={{ margin: '4px 0 12px', padding: '12px 14px', borderRadius: '18px', background: 'rgba(137,91,64,0.06)', border: '1px solid rgba(137,91,64,0.08)' }}>
      <div style={{ fontSize: '10px', color: 'var(--accent)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>{tx.orientacaoBabi}</div>
      <div style={{
        fontSize: '12px',
        color: '#6f6660',
        lineHeight: 1.55,
        overflow: 'hidden',
        display: aberta ? 'block' : '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
      }}>
        {texto}
      </div>
      {texto.length > 150 && (
        <button type="button" onClick={() => setAberta(v => !v)}
          style={{ marginTop: '7px', background: 'transparent', border: 'none', padding: 0, color: 'var(--accent)', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
          {aberta ? tx.recolher : tx.lerCompleta}
        </button>
      )}
    </div>
  );
}

function RefeicaoCard({ refeicao, abertaInicial, tx }) {
  const [aberta, setAberta] = useState(abertaInicial);
  const totalItens = refeicao.opcoes.reduce((acc, op) => acc + (op.itens?.length || 0), 0);

  return (
    <div style={{ marginBottom: '12px', background: 'rgba(255,255,255,.78)', border: '1px solid rgba(255,255,255,.72)', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 12px 28px rgba(82,69,57,.055), inset 0 1px 0 rgba(255,255,255,.84)' }}>
      <button type="button" onClick={() => setAberta(v => !v)} style={{ width: '100%', padding: '14px', display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'center', border: 0, outline: 'none', background: 'transparent', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: 650, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '8px', lineHeight: 1.2 }}>
            <span style={{ color: 'var(--accent)', display: 'flex', flexShrink: 0 }}><IconeRefeicao nome={refeicao.nome} size={17} /></span>
            <span>{refeicao.nome}</span>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-2)', marginTop: '5px' }}>
            {totalItens} itens{refeicao.horario ? ` · ${refeicao.horario}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', color: 'var(--accent)', fontSize: '12px', fontWeight: 700 }}>
          {refeicao.horario && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><IconClock size={12} />{refeicao.horario}</span>}
          <span style={{ color: 'var(--text-2)', fontSize: '18px', transform: aberta ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }}>⌄</span>
        </div>
      </button>

      {aberta && (
        <div style={{ padding: '0 14px 14px' }}>
          <NotaBabi texto={refeicao.observacao} tx={tx} />
          {refeicao.opcoes.map((op, oi) => (
            <div key={oi}>
              {refeicao.opcoes.length > 1 && <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '10px 0 2px' }}>{op.titulo}</div>}
              {op.itens.map((item, i) => {
                const qtd = formatQuantidade(item);
                return (
                  <div key={item.id || i} style={{ padding: '13px 0', borderTop: (i > 0 || refeicao.opcoes.length > 1) ? '1px solid rgba(37,28,25,0.05)' : '1px solid rgba(37,28,25,0.05)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '34px minmax(0,1fr)', gap: '10px', alignItems: 'start' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(137,91,64,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}>
                        <IconeAlimento nome={item.nome} size={15} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'grid', gap: '3px' }}>
                          <span style={{ minWidth: 0, fontSize: '13.5px', color: 'var(--text)', fontWeight: 620, lineHeight: 1.32, overflowWrap: 'anywhere' }}>{item.nome}</span>
                          {qtd && <span style={{ fontSize: '11.5px', color: 'var(--text-2)', fontWeight: 500, lineHeight: 1.35, overflowWrap: 'anywhere' }}>{qtd}</span>}
                        </div>
                        {item.observacao && <div style={{ fontSize: '11px', color: 'var(--text-2)', marginTop: '3px', fontStyle: 'italic', lineHeight: 1.4 }}>{item.observacao}</div>}
                        {item.substituicoes?.length > 0 && (
                          <div style={{ marginTop: '9px', padding: '8px 0 0 10px', borderLeft: '1px solid rgba(137,91,64,0.18)' }}>
                            <div style={{ fontSize: '10px', color: 'var(--text-2)', marginBottom: '5px', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 650 }}>{tx.trocarPor}</div>
                            <div style={{ display: 'grid', gap: '4px' }}>
                              {item.substituicoes.map((sub, si) => {
                                const subQtd = formatQuantidade(sub);
                                return (
                                  <span key={sub.id || si} style={{ display: 'grid', gridTemplateColumns: '12px minmax(0,1fr)', gap: '6px', alignItems: 'start', fontSize: '11.5px', color: 'var(--accent)', fontWeight: 520, lineHeight: 1.42 }}>
                                    <IconRefreshCw size={10} />
                                    <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{sub.nome}{subQtd && <span style={{ color: 'var(--text-2)', fontWeight: 400 }}> · {subQtd}</span>}</span>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlanoAlimentarPreview({ dieta, lang = 'pt', alunaNome }) {
  const tx = TX[lang] || TX.pt;
  const refeicoes = normalizarRefeicoes(dieta.refeicoes);
  const refeicaoAbertaIndex = proximaRefeicaoIndex(refeicoes);
  const fmtData = (iso) => new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <div className="treino-card">
      {dieta.criadoEm && <div style={{ fontSize: '11px', color: 'var(--text-2)', marginBottom: '6px' }}>{tx.cadastradoEm} {fmtData(dieta.criadoEm)}</div>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '4px' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '18px', fontWeight: 700, color: 'var(--text)' }}>{dieta.titulo}</div>
        <BaixarPdfBtn label="PDF" onClick={() => gerarPdfPlanoAlimentar(dieta, refeicoes, alunaNome || '')} />
      </div>
      {dieta.descricao && <div style={{ fontSize: '12px', color: 'var(--text-2)', marginBottom: '16px' }}>{dieta.descricao}</div>}

      {(dieta.calorias || dieta.proteina) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
          {dieta.calorias && <div style={{ padding: '14px', background: 'var(--surface, #fff)', borderRadius: '16px', boxShadow: 'var(--sh-card, var(--sh-card))', textAlign: 'center' }}><div style={{ display: 'flex', justifyContent: 'center', color: 'var(--accent)' }}><IconFlame size={18} /></div><div style={{ fontFamily: "'Inter',sans-serif", fontSize: '15px', fontWeight: 700, color: 'var(--text)', lineHeight: 1.3, marginTop: '4px' }}>{dieta.calorias}</div><div style={{ fontSize: '10px', color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '2px' }}>{tx.calorias}</div></div>}
          {dieta.proteina && <div style={{ padding: '14px', background: 'var(--surface, #fff)', borderRadius: '16px', boxShadow: 'var(--sh-card, var(--sh-card))', textAlign: 'center' }}><div style={{ display: 'flex', justifyContent: 'center', color: 'var(--accent)' }}><IconDumbbell size={18} /></div><div style={{ fontFamily: "'Inter',sans-serif", fontSize: '15px', fontWeight: 700, color: 'var(--text)', lineHeight: 1.3, marginTop: '4px' }}>{dieta.proteina}</div><div style={{ fontSize: '10px', color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '2px' }}>{tx.proteina}</div></div>}
        </div>
      )}

      <AguaCard dieta={dieta} tx={tx} />

      <div className="section-lbl">{tx.refeicoes} <div className="section-line" /></div>
      {refeicoes.map((ref, idx) => (
        <RefeicaoCard key={ref.id || idx} refeicao={ref} abertaInicial={idx === refeicaoAbertaIndex} tx={tx} />
      ))}
    </div>
  );
}
